import { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { DownstreamConfirmDialog } from '@/components/corrections/DownstreamConfirmDialog';
import {
  useCorrectReceivingLine,
  usePreviewReceivingCorrection,
  type DownstreamPreview,
  type ReceivingCorrectionInput,
} from '@/features/corrections/hooks';
import { useLocations } from '@/features/inventory/hooks';
import {
  RECEIPT_STATUS_LABELS,
  type ReceiptStatus,
  type ReceivingLineRow,
} from '@/features/procurement/hooks';
import { formatError } from '@/lib/errors';
import { centsToInputUnitDollars, formatDate, inputDollarsToUnitCents } from '@/lib/formatters';
import { formatQtyUom, isPackUnit, toStockQty } from '@/lib/uom';

const NONE = '__none';

interface EditReceivingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: ReceivingLineRow | null;
  onPrint?: (line: ReceivingLineRow) => void;
}

function localDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function receiptStatus(line: ReceivingLineRow | null): ReceiptStatus {
  if (line?.status === 'reversed' || line?.goods_receipts?.status === 'reversed') return 'reversed';
  if (line?.goods_receipts?.status === 'partially_reversed') return 'partially_reversed';
  return 'posted';
}

/**
 * Correct one received line. Quantity, component, and location changes
 * rewrite the receipt and the stock movement behind it. Price changes
 * update cost history and, when the receipt still matches the PO, the
 * PO price. Reversing the line or the whole receipt pulls stock back out.
 */
export function EditReceivingDialog({
  open,
  onOpenChange,
  line,
  onPrint,
}: EditReceivingDialogProps) {
  const preview = usePreviewReceivingCorrection();
  const correct = useCorrectReceivingLine();
  const { data: locations } = useLocations({ includeInactive: true });

  const [itemId, setItemId] = useState('');
  const [itemLabel, setItemLabel] = useState('');
  const [changingItem, setChangingItem] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [locationId, setLocationId] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');
  const [receivedOn, setReceivedOn] = useState('');
  const [packingSlip, setPackingSlip] = useState('');
  const [receiptNotes, setReceiptNotes] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<DownstreamPreview | null>(null);
  const [pendingInput, setPendingInput] = useState<ReceivingCorrectionInput | null>(null);

  useEffect(() => {
    if (!open || !line) return;
    setItemId(line.item_id ?? '');
    setItemLabel(
      line.items
        ? `${line.items.sku} — ${line.items.name}`
        : line.po_lines?.description
          ? `Custom — ${line.po_lines.description}`
          : 'Custom item',
    );
    setChangingItem(false);
    setQuantity(String(line.quantity));
    setLocationId(line.to_location_id ?? line.locations?.id ?? '');
    setUnitCost(line.unit_cost_cents == null ? '' : centsToInputUnitDollars(line.unit_cost_cents));
    setNotes(line.notes ?? '');
    setReceivedOn(localDate(line.goods_receipts?.received_at));
    setPackingSlip(line.goods_receipts?.packing_slip_number ?? '');
    setReceiptNotes(line.goods_receipts?.notes ?? '');
    setConfirmOpen(false);
    setPendingPreview(null);
    setPendingInput(null);
  }, [open, line]);

  const reversed = line?.status === 'reversed';
  const status = receiptStatus(line);
  const receiptNo = line?.goods_receipts?.receipt_number ?? 'Receipt';
  const poNo = line?.goods_receipts?.purchase_orders?.po_number;

  const buildInput = (flags?: {
    reverse?: boolean;
    reverseReceipt?: boolean;
  }): ReceivingCorrectionInput | null => {
    if (!line) return null;
    let cost: number | null;
    try {
      cost = inputDollarsToUnitCents(unitCost);
    } catch (err) {
      toast.error(formatError(err, 'Check the unit cost.'));
      return null;
    }
    const qty = Number(quantity);
    if (!flags?.reverse && !flags?.reverseReceipt && (!Number.isInteger(qty) || qty <= 0)) {
      toast.error('Quantity must be a whole number above zero. Reverse the line to remove it.');
      return null;
    }
    if (!flags?.reverse && !flags?.reverseReceipt && !locationId) {
      toast.error('Pick a location.');
      return null;
    }
    const originalDate = localDate(line.goods_receipts?.received_at);
    return {
      gr_line_id: line.id,
      quantity: flags?.reverse || flags?.reverseReceipt ? line.quantity : qty,
      item_id: itemId || line.item_id || undefined,
      to_location_id: locationId || undefined,
      unit_cost_cents: cost,
      notes: notes.trim() || null,
      packing_slip_number: packingSlip.trim() || null,
      receipt_notes: receiptNotes.trim() || null,
      ...(receivedOn && receivedOn !== originalDate ? { received_at: receivedOn } : {}),
      reverse: flags?.reverse ?? false,
      reverse_receipt: flags?.reverseReceipt ?? false,
    };
  };

  const run = async (input: ReceivingCorrectionInput) => {
    try {
      const result = await preview.mutateAsync(input);
      if (result.severity === 'none') {
        await correct.mutateAsync(input);
        toast.success(`${receiptNo} updated`);
        onOpenChange(false);
        return;
      }
      setPendingInput(input);
      setPendingPreview(result);
      setConfirmOpen(true);
    } catch (err) {
      toast.error(formatError(err, 'Could not prepare that change.'));
    }
  };

  const confirm = async () => {
    if (!pendingInput) return;
    try {
      await correct.mutateAsync(pendingInput);
      toast.success(
        pendingInput.reverse || pendingInput.reverse_receipt
          ? `${receiptNo} reversed`
          : `${receiptNo} updated`,
      );
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not apply that change.'));
    }
  };

  const busy = preview.isPending || correct.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {receiptNo}
              {poNo ? ` · ${poNo}` : ''}
            </DialogTitle>
          </DialogHeader>

          {line && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge
                  variant={
                    status === 'posted' ? 'success' : status === 'reversed' ? 'muted' : 'warning'
                  }
                >
                  {RECEIPT_STATUS_LABELS[status]}
                </Badge>
                <span className="text-muted-foreground">
                  {formatDate(line.goods_receipts?.received_at)}
                </span>
                {line.goods_receipts?.purchase_orders?.vendors?.name && (
                  <span>{line.goods_receipts.purchase_orders.vendors.name}</span>
                )}
              </div>

              {reversed ? null : (
                <>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Item</Label>
                      <button
                        type="button"
                        className="text-xs font-medium text-muted-foreground hover:text-foreground"
                        onClick={() => setChangingItem((v) => !v)}
                      >
                        {changingItem ? 'Keep current item' : 'Change item'}
                      </button>
                    </div>
                    {changingItem ? (
                      <ItemPicker
                        label=""
                        value={itemId || null}
                        onChange={(item) => {
                          if (!item) return;
                          setItemId(item.id);
                          setItemLabel(`${item.sku} — ${item.name}`);
                          setChangingItem(false);
                        }}
                      />
                    ) : (
                      <p className="text-sm">{itemLabel || '—'}</p>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="rcv-edit-qty">
                        Quantity{line?.po_lines?.uom ? ` (${line.po_lines.uom})` : ''}
                      </Label>
                      <Input
                        id="rcv-edit-qty"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value.replace(/[^\d]/g, ''))}
                        inputMode="numeric"
                        className="tabular-nums"
                      />
                      {line && line.items && isPackUnit(Number(line.conversion_to_base)) && (
                        <p className="text-xs tabular-nums text-muted-foreground">
                          = {formatQtyUom(toStockQty(Number(quantity) || 0, Number(line.conversion_to_base)), line.items.base_uom)}{' '}
                          in stock (1 {line.po_lines?.uom} ={' '}
                          {formatQtyUom(Number(line.conversion_to_base), line.items.base_uom)})
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rcv-edit-loc">Location</Label>
                      <Select
                        value={locationId || NONE}
                        onValueChange={(v) => setLocationId(v === NONE ? '' : v)}
                      >
                        <SelectTrigger id="rcv-edit-loc">
                          <SelectValue placeholder="Pick a location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Pick a location</SelectItem>
                          {(locations ?? [])
                            .filter((l) => l.is_active || l.id === locationId)
                            .map((l) => (
                              <SelectItem key={l.id} value={l.id}>
                                {l.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rcv-edit-cost">
                        Unit cost{line?.po_lines?.uom ? ` (per ${line.po_lines.uom})` : ''}
                      </Label>
                      <Input
                        id="rcv-edit-cost"
                        value={unitCost}
                        onChange={(e) => setUnitCost(e.target.value)}
                        inputMode="decimal"
                        placeholder="0.00"
                        className="tabular-nums"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rcv-edit-date">Received on</Label>
                      <Input
                        id="rcv-edit-date"
                        type="date"
                        value={receivedOn}
                        onChange={(e) => setReceivedOn(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="rcv-edit-slip">Packing slip #</Label>
                      <Input
                        id="rcv-edit-slip"
                        value={packingSlip}
                        onChange={(e) => setPackingSlip(e.target.value)}
                        maxLength={120}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="rcv-edit-notes">Line notes</Label>
                    <Textarea
                      id="rcv-edit-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rcv-edit-rnotes">Receipt notes</Label>
                    <Textarea
                      id="rcv-edit-rnotes"
                      value={receiptNotes}
                      onChange={(e) => setReceiptNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="mt-6 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {line && onPrint && (
                <Button type="button" variant="outline" onClick={() => onPrint(line)}>
                  <Printer className="h-4 w-4" />
                  Print / PDF
                </Button>
              )}
              {!reversed && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      const input = buildInput({ reverse: true });
                      if (input) void run(input);
                    }}
                  >
                    Reverse line
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      const input = buildInput({ reverseReceipt: true });
                      if (input) void run(input);
                    }}
                  >
                    Reverse receipt
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Close
              </Button>
              {!reversed && (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const input = buildInput();
                    if (input) void run(input);
                  }}
                >
                  {busy ? 'Saving…' : 'Save changes'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DownstreamConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        preview={pendingPreview}
        confirming={correct.isPending}
        onConfirm={() => void confirm()}
      />
    </>
  );
}
